import { imageFormats, variantsByFormat, type BrandingImage } from '@fms/contracts';
import { BrandMark } from './BrandMark';
import { useScheme } from '../theme/theme-init';

/**
 * The one place a logo is rendered. Every surface that used to hard-code a
 * shield glyph or a letter tile goes through this, so uploading a logo in the
 * admin branding screen changes the sidebar, both app bars, both login cards and
 * the forgot-password card at once.
 *
 * Performance shape, in order of importance:
 *  - Nothing is requested at all until an operator has uploaded something — the
 *    default is the inline vector mark.
 *  - `<picture>` offers AVIF before WebP and lets the browser pick a rendition
 *    from the width descriptors, so a 32px rail never downloads a 384px logo.
 *  - `width`/`height` are always set from the stored intrinsic size, so the box
 *    is reserved before the bytes land (no CLS).
 *  - Everything except the login card is `loading="lazy"`; the login card passes
 *    `priority` so its logo is not queued behind the hero photograph.
 */
export interface BrandLogoProps {
  /** Light-scheme logo. Null/undefined renders the built-in mark. */
  image?: BrandingImage | null;
  /** Optional light-ink variant used when the dark scheme is active. */
  imageDark?: BrandingImage | null;
  /** Absolute base URL that an `assetId` is appended to. */
  assetBaseUrl?: string;
  /** Rendered height in px. Width follows the stored aspect ratio. */
  size?: number;
  /** Accessible name. Omit when a visible wordmark sits beside the logo. */
  alt?: string;
  /** Fetch eagerly at high priority — for the logo above the fold on login. */
  priority?: boolean;
  className?: string;
}

export function BrandLogo({
  image,
  imageDark,
  assetBaseUrl,
  size = 40,
  alt,
  priority = false,
  className,
}: BrandLogoProps) {
  const scheme = useScheme();
  const chosen = (scheme === 'dark' ? (imageDark ?? image) : image) ?? null;

  if (!chosen || !assetBaseUrl) {
    return <BrandMark size={size} {...(alt ? { title: alt } : {})} className={className} />;
  }

  const url = (assetId: string) => `${assetBaseUrl.replace(/\/$/, '')}/${assetId}`;
  const ratio = chosen.width > 0 ? chosen.height / chosen.width : 1;
  const height = size;
  const width = Math.round(size / (ratio || 1));

  const webp = variantsByFormat(chosen, 'webp');
  const fallback = (
    webp.length > 0 ? webp : [...chosen.variants].sort((a, b) => a.width - b.width)
  ).slice(-1)[0];

  return (
    <picture className={className}>
      {imageFormats(chosen).map((format) => {
        const variants = variantsByFormat(chosen, format);
        if (variants.length === 0) return null;
        return (
          <source
            key={format}
            type={`image/${format}`}
            srcSet={variants.map((v) => `${url(v.assetId)} ${v.width}w`).join(', ')}
            sizes={`${width}px`}
          />
        );
      })}
      <img
        src={fallback ? url(fallback.assetId) : ''}
        alt={alt ?? ''}
        width={width}
        height={height}
        decoding="async"
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'auto'}
        style={{ display: 'block', height, width: 'auto', objectFit: 'contain' }}
      />
    </picture>
  );
}
